import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { IHierarchicalNode } from '../../models/hierarchical-node';
import { BaseType } from '../../models/space-types.enum';
import { SubscriptionAccessSource } from '../../models/subscription-access-source';

export interface IMenuItem extends IHierarchicalNode {
  baseType: string;
  route?: string;
  queryParams?: Params;
  children: IMenuItem[];
  parent?: IMenuItem;
  selected: boolean;
  selectable: boolean;
  collapsed: boolean;
  collapseDisabled: boolean;
  subscriptionAccessSource?: SubscriptionAccessSource;
  visible?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MenuItemService {
  /**
   * Get all selected items from a menu hierarchy
   */
  public getSelectedItems(items: IMenuItem[]): IMenuItem[] {
    return items
      ?.map((item) => {
        let selected = [];
        if (item.selected) selected.push(item);
        if (item.children) selected = selected.concat(this.getSelectedItems(item.children));
        return selected;
      })
      .flat();
  }

  /**
   * Find items by their IDs in the menu hierarchy
   */
  public getItemsById(items: IMenuItem[], ids: string[]): IMenuItem[] {
    return items
      ?.map((item) => {
        let matchingIds = [];
        if (ids.includes(item.id)) matchingIds.push(item);
        if (item.children) matchingIds = matchingIds.concat(this.getItemsById(item.children, ids));
        return matchingIds;
      })
      .flat();
  }

  /**
   * Disable items based on comparison type
   */
  public disableBasedOnType(
    items: IMenuItem[] | undefined,
    comparisonType?: string,
    allowedBaseTypes?: BaseType[],
  ): void {
    if (!comparisonType && !allowedBaseTypes) {
      items?.forEach((item) => {
        item.selectable = true;
        this.disableBasedOnType(item.children);
      });
      return;
    }
    items?.forEach((item) => {
      const isAllowedByBaseType = !!allowedBaseTypes?.includes(item.baseType as BaseType);
      const isAllowedByComparisonType = item.baseType === comparisonType;
      item.selectable = isAllowedByBaseType || isAllowedByComparisonType;

      this.disableBasedOnType(item.children, comparisonType, allowedBaseTypes);
    });
  }

  /**
   * Disable all unselected items
   */
  public disableUnselected(items: IMenuItem[] | undefined): void {
    items?.forEach((item) => {
      item.selectable = item.selected;
      this.disableUnselected(item.children);
    });
  }

  /**
   * Expand menu items if they contain selected children
   */
  public expandIfSelected(item: IMenuItem): boolean {
    if (item.selected) {
      return true;
    }

    const isSelected = item.children
      ? item.children.map((child) => this.expandIfSelected(child)).includes(true)
      : false;
    if (isSelected) {
      item.collapsed = false;
    }
    return isSelected;
  }

  /**
   * Deep clone menu items preserving all state
   */
  public deepCloneMenuItems(items: IMenuItem[]): IMenuItem[] {
    return items.map((item) => ({
      ...item,
      children: item.children ? this.deepCloneMenuItems(item.children) : [],
      visible: true,
    }));
  }

  /**
   * Reset visibility on all items
   */
  public resetVisibility(items: IMenuItem[]): void {
    items.forEach((item) => {
      item.visible = true;
      if (item.children) {
        this.resetVisibility(item.children);
      }
    });
  }

  /**
   * Sync selection state from source items to target items
   */
  public syncSelectionState(sourceItems: IMenuItem[], targetItems: IMenuItem[]): void {
    const sourceMap = new Map<string, boolean>();

    // Build a map of selection states from source items
    const buildSelectionMap = (items: IMenuItem[]): void => {
      items.forEach((item) => {
        sourceMap.set(item.id, item.selected);
        if (item.children) {
          buildSelectionMap(item.children);
        }
      });
    };

    // Apply selection states to target items
    const applySelectionState = (items: IMenuItem[]): void => {
      items.forEach((item) => {
        const selectedState = sourceMap.get(item.id);
        if (selectedState !== undefined) {
          item.selected = selectedState;
        }
        if (item.children) {
          applySelectionState(item.children);
        }
      });
    };

    buildSelectionMap(sourceItems);
    applySelectionState(targetItems);
  }

  /**
   * Apply filter to menu items recursively
   * @returns true if a child item matches the filter (to expand parents)
   */
  public applyFilter(items: IMenuItem[], predicate: (item: IMenuItem) => boolean, expandChildren: boolean): boolean {
    let hasVisibleChildren = false;

    for (const item of items) {
      const matches = predicate(item);
      const hasMatchingChildren = item.children ? this.applyFilter(item.children, predicate, expandChildren) : false;

      const match = matches || hasMatchingChildren;
      item.visible = match;
      if (!match) {
        continue;
      }

      // If this item matches the predicate, make ALL its children visible
      if (expandChildren && matches && item.children) {
        this.makeAllChildrenVisible(item.children);
      }

      // Expand to show matching items
      if (hasMatchingChildren) {
        item.collapsed = false;
      }

      hasVisibleChildren = true;
    }

    return hasVisibleChildren;
  }

  /**
   * Make all children visible (used when parent matches search)
   */
  private makeAllChildrenVisible(items: IMenuItem[]): void {
    for (const item of items) {
      item.visible = true;
      if (item.children) {
        this.makeAllChildrenVisible(item.children);
      }
    }
  }
}
